import React, { useState } from "react";
import { X, User as UserIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * SpocInlineModal — light-weight "+ Add SPOC" popover used from the JD
 * empty-state (V1.5.5.13). Skips the round-trip to the Client page so
 * recruiters can seed a missing office contact in-flow.
 *
 * Props:
 *  - clientId, locationId, locationLabel — where the SPOC will be created
 *  - open, onClose — modal control
 *  - onCreated(spoc) — callback fires after successful POST; parent uses
 *    it to refresh the job so the new SPOC lights up on the office card.
 */
const DESIGNATIONS = [
    "HR Manager", "Recruiter", "Hiring Manager", "Talent Acquisition",
    "Founder", "CTO", "Engineering Lead", "Interview Coordinator", "Other",
];

export default function SpocInlineModal({
    clientId,
    locationId,
    locationLabel,
    open,
    onClose,
    onCreated,
}) {
    const [form, setForm] = useState({
        name: "",
        designation: "HR Manager",
        phone: "",
        email: "",
        is_primary: true, // first SPOC on the office defaults to primary
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    if (!open) return null;

    const disabled = !form.name.trim() || !form.phone.trim() || !form.email.trim();

    const submit = async (e) => {
        e?.preventDefault();
        if (disabled) return;
        setErr("");
        setSaving(true);
        try {
            const r = await api.post(
                `/clients/${clientId}/locations/${locationId}/spocs`,
                form,
            );
            toast.success(`SPOC ${r.data.name} added to ${locationLabel}`);
            onCreated?.(r.data);
            onClose?.();
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="spoc-inline-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-md shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-cobalt text-white flex items-center justify-center">
                            <UserIcon size={13} weight="fill" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                Add SPOC to office
                            </p>
                            <p className="text-sm font-bold text-ink">{locationLabel}</p>
                        </div>
                    </div>
                    <button
                        data-testid="spoc-inline-close"
                        onClick={onClose}
                        className="p-1 hover:bg-[#F5F5F5]"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>

                <form onSubmit={submit} className="p-4 space-y-3">
                    {err && (
                        <p
                            data-testid="spoc-inline-error"
                            className="text-xs text-red-700 bg-red-50 border border-red-100 p-2"
                        >
                            {err}
                        </p>
                    )}
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">
                            Full name *
                        </label>
                        <input
                            data-testid="spoc-inline-name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Ravi Kumar"
                            className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">
                            Designation
                        </label>
                        <select
                            data-testid="spoc-inline-designation"
                            value={form.designation}
                            onChange={(e) => setForm({ ...form, designation: e.target.value })}
                            className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt bg-white"
                        >
                            {DESIGNATIONS.map((d) => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">
                                Phone *
                            </label>
                            <input
                                data-testid="spoc-inline-phone"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                placeholder="+91 98765 43210"
                                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">
                                Email *
                            </label>
                            <input
                                data-testid="spoc-inline-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="ravi@client.com"
                                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                            />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-[#525252] pt-1 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="spoc-inline-primary"
                            checked={form.is_primary}
                            onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                            className="accent-cobalt"
                        />
                        Mark as the office&apos;s primary hiring contact
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            data-testid="spoc-inline-cancel"
                            onClick={onClose}
                            className="text-xs font-bold uppercase tracking-wider text-[#525252] px-3 py-2"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="spoc-inline-submit"
                            disabled={disabled || saving}
                            className="text-xs font-black uppercase tracking-wider bg-cobalt text-white px-4 py-2 disabled:opacity-40"
                        >
                            {saving ? "Saving…" : "Add SPOC"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
